/**
 * Chutes de neige : quelques milliers de flocons dans une boîte qui suit la
 * caméra. Position, chute, dérive au vent et bouclage sont calculés dans le
 * shader à partir de l'index d'instance : zéro coût CPU par flocon.
 */
import * as THREE from 'three/webgpu';
import { float, hash, instanceIndex, mix, mod, positionLocal, smoothstep, uniform, uv, vec3, length } from 'three/tsl';

const BOX = 150;
const MAX = 7000;

export class Snowfall {
  readonly mesh: THREE.Mesh;
  private readonly geo: THREE.InstancedBufferGeometry;
  private readonly uTime = uniform(0);
  private readonly uCam = uniform(new THREE.Vector3());
  private readonly uRight = uniform(new THREE.Vector3(1, 0, 0));
  private readonly uUp = uniform(new THREE.Vector3(0, 1, 0));
  private readonly uWind = uniform(new THREE.Vector3());
  private time = 0;

  constructor(scene: THREE.Scene) {
    const quad = new THREE.PlaneGeometry(1, 1);
    const geo = new THREE.InstancedBufferGeometry();
    geo.setIndex(quad.index);
    geo.setAttribute('position', quad.attributes.position as THREE.BufferAttribute);
    geo.setAttribute('uv', quad.attributes.uv as THREE.BufferAttribute);
    geo.instanceCount = 0;
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e7);
    this.geo = geo;
    const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false });
    mat.fog = false;
    const id = float(instanceIndex);
    const rx = hash(id);
    const ry = hash(id.add(17.3));
    const rz = hash(id.add(41.7));
    const rs = hash(id.add(91.1));
    const fall = this.uTime.mul(mix(float(1.0), float(1.9), rs));
    const sway = this.uTime.mul(mix(float(0.6), float(1.4), rz)).add(rx.mul(40)).sin().mul(0.6);
    const p = vec3(rx.mul(BOX).add(sway), ry.mul(BOX).sub(fall), rz.mul(BOX)).add(this.uWind.mul(this.uTime));
    const rel = mod(p.sub(this.uCam).add(BOX / 2), float(BOX)).sub(BOX / 2);
    const centre = this.uCam.add(rel);
    const size = mix(float(0.08), float(0.17), rs);
    const offset = this.uRight.mul(positionLocal.x).add(this.uUp.mul(positionLocal.y)).mul(size);
    mat.positionNode = centre.add(offset);
    mat.colorNode = vec3(1, 1, 1);
    // Flocon rond à bord doux, atténué près des bords de la boîte (pas d'apparition brutale).
    const round = float(1).sub(smoothstep(float(0.18), float(0.5), length(uv().sub(0.5))));
    mat.opacityNode = float(0.9).mul(smoothstep(float(BOX / 2), float(BOX / 2 - 25), length(rel))).mul(round);
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    this.mesh.name = 'snowfall';
    this.mesh.renderOrder = 10;
    scene.add(this.mesh);
  }

  /** rate : chutes en cm/h ; wind en km/h. */
  update(dt: number, camera: THREE.Camera, rate: number, wind: number, enabled: boolean): void {
    const n = enabled ? Math.round(Math.min(1, rate / 2.5) * MAX) : 0;
    this.geo.instanceCount = n;
    this.mesh.visible = n > 0;
    if (!n) return;
    this.time += dt;
    this.uTime.value = this.time;
    (this.uCam.value as THREE.Vector3).copy(camera.position);
    const m = camera.matrixWorld.elements;
    (this.uRight.value as THREE.Vector3).set(m[0] as number, m[1] as number, m[2] as number);
    (this.uUp.value as THREE.Vector3).set(m[4] as number, m[5] as number, m[6] as number);
    (this.uWind.value as THREE.Vector3).set((wind / 3.6) * 0.35, 0, (wind / 3.6) * 0.12);
  }
}
